import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  MicrophoneIcon, 
  VideoCameraIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  ComputerDesktopIcon,
  // SpeakerWaveIcon, // speech option disabled
  ChatBubbleLeftRightIcon,
  InformationCircleIcon,
  PlayIcon,
  StopIcon
} from '@heroicons/react/24/outline'
import { toast } from 'sonner'
import { apiUrl } from '@/utils/api'
import clsx from 'clsx'

import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import NoiseConsentModal from '@/components/ui/NoiseConsentModal'
import SoundWaveVisualizer from '@/components/ui/SoundWaveVisualizer'
import ThemeControls from '@/components/ui/ThemeControls'
import { useTheme } from '@/contexts/ThemeContext'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { loginSuccess, storeNoiseConsent } from '@/store/slices/authSlice'
import { addNotification } from '@/store/slices/uiSlice'
import { useVADCDN } from '@/hooks/useVADCDN'
import { useAudioLevelDetection } from '@/hooks/useAudioLevelDetection'

interface NoiseTestResult {
  isNoisy: boolean
  noiseLevel: 'low' | 'moderate' | 'high'
  recommendation: string
}

const WelcomePage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { noiseConsentData } = useAppSelector(state => state.auth)
  const { fontSize } = useTheme()
  
  const [isLoading, setIsLoading] = useState(false)
  
  const [candidateInfo, setCandidateInfo] = useState({
    candidateId: '',
    organizationId: '',
    candidateName: '',
    position: '',
    employmentType: '',
    duration: '',
    psychometricResultUrl: '',
    companyProfileUrl: '',
    reportCallbackUrl: '',
  })
  
  const [permissionsGranted, setPermissionsGranted] = useState(() => {
    // Load camera/mic from localStorage. Screen share is not a sticky permission,
    // so never treat a previous visit as confirmed.
    try {
      const saved = localStorage.getItem('snwolley-media-permissions')
      const parsed = saved ? JSON.parse(saved) : {
        camera: false,
        microphone: false,
        screen: false,
      }
      return { ...parsed, screen: false }
    } catch {
      return {
        camera: false,
        microphone: false,
        screen: false,
      }
    }
  })
  
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(false)
  const [isCheckingScreenShare, setIsCheckingScreenShare] = useState(false)
  const [screenShareCheckedThisVisit, setScreenShareCheckedThisVisit] = useState(false)
  // Text-only: speech input is disabled in the UI (kept typed for easy restore)
  const [inputType, setInputType] = useState<'speech' | 'text'>('text')
  const [isTestingNoise, setIsTestingNoise] = useState(false)
  const [noiseTestResult, setNoiseTestResult] = useState<NoiseTestResult | null>(null)
  const environmentTestRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [showPermissionErrorModal, setShowPermissionErrorModal] = useState(false)
  const [permissionErrorDetails, setPermissionErrorDetails] = useState<{
    title: string
    message: string
    guidance: string
    showOsHelp?: boolean
  } | null>(null)
  const [showNoiseConsentModal, setShowNoiseConsentModal] = useState(false)
  const [hasAcceptedNoisyEnvironment, setHasAcceptedNoisyEnvironment] = useState(false)
  
  // VAD hook using CDN approach (like original implementation)
  const { isVADSupported, testEnvironment, isInitializing: vadInitializing } = useVADCDN()
  
  // Audio level detection for visual feedback during noise test
  const {
    audioLevel,
    isDetecting,
    startDetection,
    stopDetection,
    peakLevel,
    averageLevel,
    resetPeak,
    resetAll
  } = useAudioLevelDetection({
    updateInterval: 50, // 20 FPS for smooth animation
    smoothingFactor: 0.7 // Good balance of responsiveness and smoothness
  })

  // Helper function to handle permission errors with detailed guidance
  const handlePermissionError = useCallback((error: any) => {
    let errorMessage = 'Camera and microphone access are required for the interview.'
    let guidance = ''

    if (error.name === 'NotAllowedError') {
      guidance = `Permission was blocked or denied:

1. Look for a camera/microphone icon in your browser's address bar
2. Click on the icon and select "Allow" for both camera and microphone
3. Refresh the page and try again

Chrome/Edge: Look for a camera icon in the address bar (right side)
Firefox: Look for a camera icon in the address bar (left side)
Safari: Check Safari menu → Settings for This Website → Camera & Microphone

If you don't see any icons, the site permissions might be blocked in your browser settings.`
    } else if (error.name === 'NotFoundError') {
      guidance = `No camera or microphone detected:

1. Check if your camera/microphone is connected and working
2. Close other applications that might be using your camera (Zoom, Skype, etc.)
3. Try refreshing the page and grant permissions again
4. Check your system settings to ensure camera/microphone are not disabled`
    } else if (error.name === 'NotReadableError') {
      guidance = `Camera or microphone is already in use:

1. Close other applications that might be using your camera/microphone
2. Restart your browser and try again
3. Check your system settings for privacy permissions`
    } else {
      guidance = `General troubleshooting:

1. Refresh the page and try again
2. Check browser permissions in Settings → Privacy & Security → Site Settings
3. Try a different browser (Chrome, Firefox, Safari, Edge)
4. Restart your browser completely
5. Contact support if the issue persists`
    }

    // Show modal with error details
    setPermissionErrorDetails({
      title: 'Permission Required',
      message: errorMessage,
      guidance: guidance
    })
    setShowPermissionErrorModal(true)

    // Also set error state for UI display
    setError(`${errorMessage}\n${guidance}`)
  }, [])

  const handleScreenShareError = useCallback((error: any) => {
    let errorMessage = 'Screen sharing is required before you can start the interview.'
    let guidance = ''

    if (error?.name === 'NotAllowedError') {
      guidance = 'Sharing was cancelled or blocked. Choose Entire Screen when the picker appears. If it does not appear, allow screen sharing using the steps below, then refresh.'
    } else if (error?.name === 'NotFoundError' || error?.name === 'NotReadableError') {
      guidance = 'Your screen could not be captured. Close other apps that may be recording your screen, then try Chrome or Edge.'
    } else {
      guidance = 'Screen sharing could not be started. Use Chrome or Edge, choose Entire Screen, and check the permission steps below if the picker does not appear.'
    }

    setPermissionErrorDetails({
      title: 'Screen Sharing Required',
      message: errorMessage,
      guidance: guidance,
      showOsHelp: true
    })
    setShowPermissionErrorModal(true)
    setError(`${errorMessage}\n${guidance}`)
  }, [])

  // Helper function to update permissions consistently
  const updatePermissions = useCallback((updates: Partial<typeof permissionsGranted>) => {
    setPermissionsGranted(prev => {
      const newPermissions = { ...prev, ...updates }
      localStorage.setItem('snwolley-media-permissions', JSON.stringify(newPermissions))
      return newPermissions
    })
  }, [])

  const requestScreenShareCheck = useCallback(async (): Promise<boolean> => {
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 1600 },
          height: { ideal: 900 },
          frameRate: { ideal: 30 },
          displaySurface: 'monitor'
        },
        audio: true
      })

      const videoTrack = screenStream.getVideoTracks()[0]
      const isLive = Boolean(videoTrack && videoTrack.readyState === 'live')
      const displaySurface = videoTrack?.getSettings()?.displaySurface

      screenStream.getTracks().forEach(track => track.stop())

      if (!isLive) {
        setScreenShareCheckedThisVisit(false)
        updatePermissions({ screen: false })
        toast.error('Screen sharing stopped before it could be confirmed. Please try again.')
        return false
      }

      if (displaySurface !== 'monitor') {
        setScreenShareCheckedThisVisit(false)
        updatePermissions({ screen: false })
        const selected =
          displaySurface === 'browser'
            ? 'a browser tab'
            : displaySurface === 'window'
              ? 'a window'
              : 'something other than your entire screen'
        setPermissionErrorDetails({
          title: 'Entire Screen Required',
          message: 'Please share your entire screen, not a window or browser tab.',
          guidance: `You selected ${selected}. Click "Start Screen Sharing" again and choose Entire Screen / Your Entire Screen in the picker.

Chrome/Edge: open the "Entire Screen" tab at the top of the share dialog, then select your display.

Do not choose a single application window or a browser tab. You will be asked to share your entire screen again when the interview starts.`
        })
        setShowPermissionErrorModal(true)
        return false
      }

      updatePermissions({ screen: true })
      setScreenShareCheckedThisVisit(true)
      toast.success('Entire screen sharing confirmed. You will be asked to share again when the interview starts.')
      return true
    } catch (screenError) {
      console.error('Screen sharing check failed:', screenError)
      setScreenShareCheckedThisVisit(false)
      updatePermissions({ screen: false })
      handleScreenShareError(screenError)
      return false
    }
  }, [handleScreenShareError, updatePermissions])

  // Extract URL parameters on mount
  useEffect(() => {
    const candidateId = searchParams.get('candidateId') || ''
    const organizationId = searchParams.get('organizationId') || ''
    const candidateName = searchParams.get('candidateName') || ''
    const position = searchParams.get('candidatePosition') || ''
    // Support both snake_case (employment_type) and camelCase (employmentType) for backward compatibility
    const employmentType = searchParams.get('employment_type') || searchParams.get('employmentType') || 'Full-time'
    const duration = searchParams.get('duration') || ''
    const psychometricResultUrl = searchParams.get('psychometricResultUrl') || ''
    const companyProfileUrl = searchParams.get('companyProfileUrl') || ''
    const reportCallbackUrl = searchParams.get('reportCallbackUrl') || ''
    
    // Console log for debugging if needed
    // console.log('URL Parameters:', { candidateId, organizationId, candidateName, position, employmentType, duration, psychometricResultUrl, companyProfileUrl, reportCallbackUrl })
    
    setCandidateInfo({
      candidateId,
      organizationId,
      candidateName,
      position,
      employmentType,
      duration,
      psychometricResultUrl,
      companyProfileUrl,
      reportCallbackUrl,
    })
  }, [searchParams])

  // No automatic permission checking - wait for user interaction
  // This follows the pattern from the original implementation

  const requestPermissions = async () => {
    try {
      setIsCheckingPermissions(true)
      
      try {
        const cameraStream = await navigator.mediaDevices.getUserMedia({ 
          video: {
            width: 320,
            height: 240,
            frameRate: 20
          },
          audio: true
        })
        
        updatePermissions({ microphone: true, camera: true })
        
        // Stop the streams for now - will be re-acquired on interview page
        cameraStream.getTracks().forEach(track => track.stop())
        
        toast.success('Camera and microphone permissions granted!')
      } catch (cameraError) {
        console.error('Camera/microphone permission failed:', cameraError)
        handlePermissionError(cameraError)
        return
      }

      setIsCheckingPermissions(false)
      setIsCheckingScreenShare(true)
      await requestScreenShareCheck()
    } catch (error) {
      console.error('Permission request failed:', error)
      toast.error('Failed to get permissions')
    } finally {
      setIsCheckingPermissions(false)
      setIsCheckingScreenShare(false)
    }
  }

  const handleStartScreenSharing = async () => {
    try {
      setIsCheckingScreenShare(true)
      await requestScreenShareCheck()
    } finally {
      setIsCheckingScreenShare(false)
    }
  }

  const startNoiseTest = useCallback(async () => {
    if (isTestingNoise || vadInitializing) {
      return
    }

    if (!permissionsGranted.microphone) {
      toast.error('Please grant microphone permission first')
      return
    }

    if (!isVADSupported) {
      toast.error('Voice activity detection is not supported in your browser')
      return
    }

    try {
      setIsTestingNoise(true)
      setNoiseTestResult(null)
      resetAll() // Reset all audio levels for fresh test
      
      toast.info('Testing environment for background noise... Please stay quiet for 6 seconds.')
      console.log('Starting environment test...')

      // Start audio level detection for visual feedback
      let result
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        startDetection(stream)
        
        // Use real VAD for proper environment testing
        result = await testEnvironment()
        
        // Add a small delay to ensure final audio levels are captured and displayed
        await new Promise(resolve => setTimeout(resolve, 500))
        
        // Stop audio detection after test
        stopDetection()
        
        // Stop the stream
        stream.getTracks().forEach(track => track.stop())
      } catch (audioError) {
        console.error('Error getting audio stream for visualization:', audioError)
        // Fall back to VAD test without visualization
        result = await testEnvironment()
      }
      
      console.log('Environment test result:', result)
      setNoiseTestResult(result)
      
      if (result.isNoisy) {
        // Show consent modal for noisy environments
        setShowNoiseConsentModal(true)
        toast.warning('Background noise detected - please review the recommendations')
      } else {
        toast.success(`Environment test complete: ${result.recommendation}`)
      }
      
    } catch (error) {
      console.error('Noise test failed:', error)
      toast.error('Failed to test audio environment. Please try again.')
    } finally {
      setIsTestingNoise(false)
    }
  }, [isTestingNoise, vadInitializing, permissionsGranted.microphone, isVADSupported, testEnvironment])

  // No cleanup needed - VAD hook handles its own cleanup

  const startInterview = async () => {
    // Validate required parameters
    if (!candidateInfo.candidateId || !candidateInfo.organizationId) {
      toast.error('We are not able to process your request because: Missing required parameters')
      return
    }

    if (!permissionsGranted.microphone || !permissionsGranted.camera) {
      toast.error('Camera and microphone permissions are required to start the interview')
      return
    }

    if (!screenShareCheckedThisVisit) {
      toast.error('Please start screen sharing before beginning the interview')
      return
    }

    // Check for duplicate interview before proceeding
    try {
      const duplicateParams = new URLSearchParams({
        candidate_id: candidateInfo.candidateId,
        organization_id: candidateInfo.organizationId,
        position: candidateInfo.position || '',
        employment_type: candidateInfo.employmentType || 'Full-time'
      })
      
      const duplicateResponse = await fetch(apiUrl(`/api/v1/interview/check-duplicate?${duplicateParams}`))
      
      if (!duplicateResponse.ok) {
        console.warn('Unable to check for duplicate interviews, proceeding anyway')
      } else {
        const duplicateData = await duplicateResponse.json()
        
        if (duplicateData.has_completed) {
          toast.error(duplicateData.message)
          return
        }
      }
    } catch (duplicateError) {
      console.error('Error checking for duplicate interviews:', duplicateError)
      // Continue anyway, don't block the interview for this check
    }

    // Speech mode disabled — environment/noise gate commented out
    // if (inputType === 'speech') {
    //   if (!noiseTestResult) {
    //     toast.error('Please run the environment test first before starting speech mode.')
    //     return
    //   }
    //   if (noiseTestResult.isNoisy && !hasAcceptedNoisyEnvironment) {
    //     toast.error('Please acknowledge the environment conditions or switch to text mode.')
    //     setShowNoiseConsentModal(true)
    //     return
    //   }
    // }

    try {
      setIsLoading(true)
      
      // Validate organization exists in database and check duration limits
      try {
        const orgResponse = await fetch(apiUrl(`/api/v1/organizations/${candidateInfo.organizationId}/validate`));
        if (!orgResponse.ok) {
          if (orgResponse.status === 404) {
            toast.error(`Organization '${candidateInfo.organizationId}' not found. Please contact support.`)
            return
          }
          throw new Error('Organization validation failed')
        }
        const orgData = await orgResponse.json()
        console.log('Organization validated:', orgData)

        // Validate interview duration against organization's maximum
        const requestedDurationMinutes = candidateInfo.duration ? parseInt(candidateInfo.duration) : 30 // default 30 minutes
        const maxDurationMinutes = orgData.max_interview_duration || 60 // default 60 minutes
        
        if (requestedDurationMinutes > maxDurationMinutes) {
          toast.error(
            `Interview duration of ${requestedDurationMinutes} minutes exceeds the maximum allowed duration of ${maxDurationMinutes} minutes for ${candidateInfo.organizationId}. Please contact your hiring manager.`
          )
          return
        }
        
        console.log(`Duration validation passed: ${requestedDurationMinutes}min <= ${maxDurationMinutes}min`)
      } catch (orgError) {
        console.error('Organization validation error:', orgError)
        toast.error(`Unable to validate organization '${candidateInfo.organizationId}'. Please check your URL parameters or contact support.`)
        return
      }
      
      // Create anonymous token for interview session
      console.log('Creating anonymous token...')
      const tokenResponse = await fetch(apiUrl('/api/v1/auth/anonymous-token'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          candidate_id: candidateInfo.candidateId,
          organization: candidateInfo.organizationId,
          candidate_name: candidateInfo.candidateName,
          position: candidateInfo.position
        })
      })

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json()
        throw new Error(errorData.detail || 'Failed to create authentication token')
      }

      const tokenResult = await tokenResponse.json()
      const authToken = tokenResult.access_token
      console.log('Anonymous token created successfully')

      // Store candidate info in Redux with real token
      dispatch(loginSuccess({
        token: authToken,
        candidateId: candidateInfo.candidateId,
        organizationId: candidateInfo.organizationId,
        candidateName: candidateInfo.candidateName,
        position: candidateInfo.position,
        employmentType: candidateInfo.employmentType || 'Full-time',
        duration: candidateInfo.duration,
        psychometricResultUrl: candidateInfo.psychometricResultUrl,
        companyProfileUrl: candidateInfo.companyProfileUrl,
        reportCallbackUrl: candidateInfo.reportCallbackUrl,
        expiresIn: 3600, // 1 hour
      }))

      // Store input type preference (always text — speech UI disabled)
      localStorage.setItem('interviewInputType', 'text')

      // Create interview session in database
      console.log('Creating interview session...')
      const sessionData = {
        candidate_id: candidateInfo.candidateId,
        organization_id: candidateInfo.organizationId,
        candidate_name: candidateInfo.candidateName,
        position: candidateInfo.position,
        employment_type: candidateInfo.employmentType || 'Full-time',
        interview_duration: candidateInfo.duration ? parseInt(candidateInfo.duration) * 60 : 1800, // Convert to seconds
        competency_questions_limit: 3,
        psychometric_result_url: candidateInfo.psychometricResultUrl,
        company_profile_url: candidateInfo.companyProfileUrl,
        report_callback_url: candidateInfo.reportCallbackUrl,
        // Include noise consent data if available
        noise_consent_given: noiseConsentData?.consentGiven || false,
        noise_level_detected: noiseConsentData?.noiseLevel || null,
        environment_recommendation: noiseConsentData?.recommendation || null
      }

      const sessionResponse = await fetch(apiUrl('/api/v1/interview/sessions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(sessionData)
      })

      if (!sessionResponse.ok) {
        const errorData = await sessionResponse.json()
        throw new Error(errorData.detail || 'Failed to create interview session')
      }

      const sessionResult = await sessionResponse.json()
      console.log('Interview session created:', sessionResult)
      
      // Store session ID for the interview page
      if (!sessionResult.id) {
        throw new Error('Session creation succeeded but no session ID returned')
      }
      localStorage.setItem('interview-session-id', sessionResult.id)
      console.log('Stored session ID:', sessionResult.id)

      dispatch(addNotification({
        type: 'success',
        title: 'Ready to Start',
        message: `Interview will use ${inputType} input mode.`,
      }))

      toast.success('Starting interview...')
      
      // Navigate to interview page
      navigate('/interview')
    } catch (error) {
      console.error('Failed to start interview:', error)
      toast.error('Failed to start interview. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Consent modal handlers
  const handleAcceptNoisyEnvironment = useCallback(async () => {
    if (!noiseTestResult) return

    try {
      // Store consent in Redux to be logged when interview session is created
      dispatch(storeNoiseConsent({
        noiseLevel: noiseTestResult.noiseLevel,
        recommendation: noiseTestResult.recommendation,
        consentGiven: true
      }))

      setHasAcceptedNoisyEnvironment(true)
      setShowNoiseConsentModal(false)
      toast.success('Environment conditions acknowledged. You can now start the interview.')
      
      console.log('Noise consent stored for logging when interview starts')
    } catch (error) {
      console.error('Failed to store noise consent:', error)
      // Still allow user to proceed
      setHasAcceptedNoisyEnvironment(true)
      setShowNoiseConsentModal(false)
      toast.success('Environment conditions acknowledged. You can now start the interview.')
    }
  }, [noiseTestResult, dispatch])

  const handleRetryNoiseTest = useCallback(() => {
    setShowNoiseConsentModal(false)
    setNoiseTestResult(null)
    setHasAcceptedNoisyEnvironment(false)
    // The existing runEnvironmentTest function will be called from the UI
  }, [])

  const handleSwitchToTextMode = useCallback(() => {
    setInputType('text')
    setShowNoiseConsentModal(false)
    setNoiseTestResult(null)
    setHasAcceptedNoisyEnvironment(false)
    toast.info('Switched to text input mode')
  }, [])

  const fontClass =
    fontSize === 'small'
      ? 'font-small'
      : fontSize === 'large'
      ? 'font-large'
      : 'font-medium'

  return (
    <div className={clsx('min-h-screen flex items-center justify-center px-4', fontClass)}>
      <div className="max-w-4xl w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8 pt-8"
        >
          <div className="w-20 h-20 mx-auto mb-6 bg-white rounded-full flex items-center justify-center shadow-lg p-2">
            <img 
              src="/Snwolley_Logo.png"
              alt="Snwolley Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Welcome to <span className="text-gradient-snwolley">Snwolley</span> Interviewer
          </h1>
          <p className="text-lg text-gray-600">
            AI-Powered Interview & Assessment System
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Candidate Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="card p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Interview Details</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Candidate Name
                  </label>
                  <p className="text-gray-900 font-medium">
                    {candidateInfo.candidateName || 'Not provided'}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position
                  </label>
                  <p className="text-gray-900 font-medium">
                    {candidateInfo.position || 'Not specified'}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <p className="text-gray-900 font-medium">
                    {candidateInfo.organizationId || 'Not provided'}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employment Type
                  </label>
                  <p className="text-gray-900">
                    {candidateInfo.employmentType || 'Full-time'}
                  </p>
                </div>
              </div>
            </motion.div>


            {/* Input Type — text only (speech option commented out) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="card p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Input Type</h2>
              <p className="text-gray-600 mb-4 text-sm">You’ll respond by typing during the interview:</p>
              
              <div className="grid grid-cols-1 gap-4">
                {/* Speech option disabled
                <button
                  onClick={() => {
                    setInputType('speech')
                    setNoiseTestResult(null)
                    setHasAcceptedNoisyEnvironment(false)
                    setTimeout(() => {
                      environmentTestRef.current?.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'center' 
                      })
                    }, 200)
                  }}
                  className={clsx(
                    'p-4 rounded-lg border-2 transition-all duration-200 text-left',
                    inputType === 'speech'
                      ? 'border-snwolley-green-500 bg-snwolley-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <div className="flex items-center mb-2">
                    <SpeakerWaveIcon className="w-5 h-5 text-snwolley-green-600 mr-2" />
                    <span className="font-semibold text-gray-900">Speech</span>
                  </div>
                  <p className="text-sm text-gray-600">Speak your answers aloud</p>
                  <p className="text-xs text-gray-500 mt-1">Requires quiet environment</p>
                </button>
                */}

                <div
                  className="p-4 rounded-lg border-2 border-snwolley-green-500 bg-snwolley-green-50 text-left"
                >
                  <div className="flex items-center mb-2">
                    <ChatBubbleLeftRightIcon className="w-5 h-5 text-snwolley-green-600 mr-2" />
                    <span className="font-semibold text-gray-900">Text</span>
                  </div>
                  <p className="text-sm text-gray-600">Type your answers</p>
                  <p className="text-xs text-gray-500 mt-1">Works in any environment</p>
                </div>
              </div>

              {/* Noise Test for Speech Mode — disabled while speech is off
              {inputType === 'speech' && permissionsGranted.microphone && (
                <div ref={environmentTestRef} className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  ...
                </div>
              )}
              */}
            </motion.div>

            {/* Theme Settings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="card p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Font Settings</h2>
              <p className="text-gray-600 mb-4 text-sm">
                Adjust the font size for comfortable reading during your interview. This setting cannot be changed once the interview starts.
              </p>
              
              <ThemeControls disabled={false} />
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Interview Instructions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="card p-6"
            >
              <div className="flex items-center mb-4">
                <InformationCircleIcon className="w-6 h-6 text-snwolley-green-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">Interview Instructions</h2>
              </div>
              
              <div className="space-y-4 text-sm text-gray-700">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">📍 Environment Setup</h3>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Find a quiet, well-lit room with minimal distractions</li>
                    <li>Ensure stable internet connection</li>
                    <li>Close unnecessary applications to avoid interruptions</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">🌐 Browser Recommendations</h3>
                  <ul className="space-y-1 list-disc list-inside">
                    <li><strong>Chrome</strong> or <strong>Edge</strong> - Best overall performance and compatibility</li>
                    <li>Safari - Good support, but may have screen sharing limitations</li>
                    {/*<li>Ensure browser is updated to the latest version</li>*/}
                  </ul>
                </div>

                {/* Speech mode disabled
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">🎤 Audio Quality (For Speech Mode)</h3>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Use headphones to prevent audio feedback</li>
                    <li>Speak clearly and at a moderate pace</li>
                    <li>Avoid background noise (TV, music, conversations)</li>
                  </ul>
                </div>
                */}

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">📹 Camera Setup</h3>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Position camera at eye level</li>
                    <li>Ensure your face is well-lit and clearly visible</li>
                    <li>Maintain professional appearance</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">💻 Screen Sharing</h3>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Start screen sharing on this page before you click Start Interview</li>
                    <li>Share your entire screen (not individual windows or tabs)</li>
                    <li>Your browser will ask you to share again when the interview starts</li>
                    <li>Close sensitive or personal information</li>
                    <li>Keep your desktop organized and professional</li>
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* System Requirements */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="card p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-4">System Requirements</h2>
              
              <div className="space-y-4">
                {/* Microphone Check */}
                <div className="flex items-center">
                  <div className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center mr-3',
                    isCheckingPermissions 
                      ? 'bg-yellow-100' 
                      : permissionsGranted.microphone 
                        ? 'bg-green-100' 
                        : 'bg-red-100'
                  )}>
                    {isCheckingPermissions ? (
                      <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
                    ) : permissionsGranted.microphone ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-600" />
                    ) : (
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  
                  <div className="flex items-center flex-1">
                    <MicrophoneIcon className="w-5 h-5 text-gray-500 mr-2" />
                    <span className="text-gray-900 font-medium">
                      Microphone Access
                    </span>
                    <span className={clsx(
                      'ml-2 text-sm',
                      permissionsGranted.microphone ? 'text-green-600' : 'text-red-600'
                    )}>
                      {isCheckingPermissions 
                        ? 'Checking...' 
                        : permissionsGranted.microphone 
                          ? 'Granted' 
                          : 'Required'
                      }
                    </span>
                  </div>
                </div>

                {/* Camera Check */}
                <div className="flex items-center">
                  <div className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center mr-3',
                    isCheckingPermissions 
                      ? 'bg-yellow-100' 
                      : permissionsGranted.camera 
                        ? 'bg-green-100' 
                        : 'bg-red-100'
                  )}>
                    {isCheckingPermissions ? (
                      <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
                    ) : permissionsGranted.camera ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-600" />
                    ) : (
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  
                  <div className="flex items-center flex-1">
                    <VideoCameraIcon className="w-5 h-5 text-gray-500 mr-2" />
                    <span className="text-gray-900 font-medium">
                      Camera Access
                    </span>
                    <span className={clsx(
                      'ml-2 text-sm',
                      permissionsGranted.camera ? 'text-green-600' : 'text-red-600'
                    )}>
                      {isCheckingPermissions 
                        ? 'Checking...' 
                        : permissionsGranted.camera 
                          ? 'Granted' 
                          : 'Required'
                      }
                    </span>
                  </div>
                </div>

                {/* Screen Sharing Check */}
                <div className="flex items-center">
                  <div className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center mr-3',
                    isCheckingScreenShare
                      ? 'bg-yellow-100'
                      : screenShareCheckedThisVisit
                        ? 'bg-green-100'
                        : 'bg-red-100'
                  )}>
                    {isCheckingScreenShare ? (
                      <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
                    ) : screenShareCheckedThisVisit ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-600" />
                    ) : (
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  
                  <div className="flex items-center flex-1">
                    <ComputerDesktopIcon className="w-5 h-5 text-gray-500 mr-2" />
                    <span className="text-gray-900 font-medium">
                      Screen Sharing
                    </span>
                    <span className={clsx(
                      'ml-2 text-sm',
                      screenShareCheckedThisVisit ? 'text-green-600' : 'text-red-600'
                    )}>
                      {isCheckingScreenShare
                        ? 'Checking...'
                        : screenShareCheckedThisVisit
                          ? 'Confirmed'
                          : 'Required'
                      }
                    </span>
                  </div>
                </div>
              </div>

              {(!permissionsGranted.microphone || !permissionsGranted.camera) && !isCheckingPermissions && (
                <div className="mt-4 p-4 bg-red-50 rounded-lg">
                  <div className="flex">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mt-0.5 mr-2" />
                    <div className="text-sm text-red-800">
                      <strong>Camera, microphone, and screen sharing are required</strong> to participate in the interview.
                      Please click "Grant Media Permissions" to provide camera and microphone access, then share your screen.
                    </div>
                  </div>
                </div>
              )}

              {permissionsGranted.microphone && permissionsGranted.camera && !screenShareCheckedThisVisit && !isCheckingScreenShare && (
                <div className="mt-4 p-4 bg-red-50 rounded-lg">
                  <div className="flex">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mt-0.5 mr-2" />
                    <div className="text-sm text-red-800">
                      <strong>Screen sharing is required</strong> before you can start the interview.
                      Please click "Start Screen Sharing" and choose your entire screen. Your browser will ask you to share again when the interview begins.
                    </div>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="space-y-4"
            >
              {(!permissionsGranted.microphone || !permissionsGranted.camera) && (
                <Button
                  onClick={requestPermissions}
                  variant="secondary"
                  size="lg"
                  fullWidth
                  disabled={isCheckingPermissions || isCheckingScreenShare}
                  icon={<VideoCameraIcon className="w-5 h-5" />}
                >
                  {isCheckingPermissions ? 'Requesting Permissions...' : 'Grant Media Permissions'}
                </Button>
              )}

              {permissionsGranted.microphone && permissionsGranted.camera && !screenShareCheckedThisVisit && (
                <Button
                  onClick={handleStartScreenSharing}
                  variant="secondary"
                  size="lg"
                  fullWidth
                  disabled={isCheckingScreenShare}
                  icon={<ComputerDesktopIcon className="w-5 h-5" />}
                >
                  {isCheckingScreenShare ? 'Requesting Screen Sharing...' : 'Start Screen Sharing'}
                </Button>
              )}
              
              <Button
                onClick={startInterview}
                variant="primary"
                size="lg"
                fullWidth
                loading={isLoading}
                disabled={
                  !permissionsGranted.microphone ||
                  !permissionsGranted.camera ||
                  !screenShareCheckedThisVisit
                }
              >
                Start Interview (text mode)
              </Button>
            </motion.div>

            {/* Privacy Notice */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="text-center text-sm text-gray-500 p-4 bg-gray-50 rounded-lg"
            >
              <p>
                Your privacy is important to us. Audio, video, and screen activity will only be recorded during the interview
                and will be used solely for assessment purposes.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Permission Error Modal */}
        <Modal
          isOpen={showPermissionErrorModal}
          onClose={() => setShowPermissionErrorModal(false)}
          title={permissionErrorDetails?.title || 'Permission Error'}
          size="lg"
        >
          <div className="space-y-4">
            <div className="text-red-600 font-medium">
              {permissionErrorDetails?.message}
            </div>
            <div className="text-gray-700 whitespace-pre-line">
              {permissionErrorDetails?.guidance}
            </div>

            {permissionErrorDetails?.showOsHelp && (
              <div className="space-y-3">
                <p className="text-sm font-semibold text-gray-900">
                  Fix screen sharing permissions
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Mac</p>
                    <ol className="text-sm text-gray-700 space-y-1.5 list-decimal list-inside">
                      <li>System Settings → Privacy &amp; Security → Screen Recording</li>
                      <li>Turn on Chrome or Edge</li>
                      <li>Quit and reopen the browser, then try again</li>
                    </ol>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-sm font-semibold text-gray-900 mb-2">Windows</p>
                    <ol className="text-sm text-gray-700 space-y-1.5 list-decimal list-inside">
                      <li>Click the lock icon in the address bar → Allow screen sharing</li>
                      <li>Settings → Privacy &amp; security → Screenshots and apps → allow your browser</li>
                      <li>Restart the browser, then try again</li>
                    </ol>
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  Contact support if it still does not work.
                </p>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button
                onClick={() => setShowPermissionErrorModal(false)}
                variant="primary"
              >
                Try Again
              </Button>
            </div>
          </div>
        </Modal>

        {/* Noise Consent Modal */}
        {noiseTestResult && (
          <NoiseConsentModal
            isOpen={showNoiseConsentModal}
            onClose={handleSwitchToTextMode}
            onAcceptAndProceed={handleAcceptNoisyEnvironment}
            onRetry={handleRetryNoiseTest}
            noiseLevel={noiseTestResult.noiseLevel === 'low' ? 'moderate' : noiseTestResult.noiseLevel}
            recommendation={noiseTestResult.recommendation}
          />
        )}
      </div>
    </div>
  )
}

export default WelcomePage